import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No se encontró el token de verificación');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage(response.data.message || '¡Tu email ha sido verificado!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error al verificar el email');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-8 max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-ai-glow animate-spin" />
            <h1 className="font-heading text-2xl mb-4">Verificando tu email...</h1>
            <p className="text-secondary">Por favor espera mientras confirmamos tu cuenta</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-success/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-success" />
            </div>
            <h1 className="font-heading text-2xl mb-4">¡Email verificado!</h1>
            <p className="text-secondary mb-8">{message}</p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-white text-black hover:bg-gray-200 rounded-full px-8"
            >
              Iniciar sesión
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-error/20 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-error" />
            </div>
            <h1 className="font-heading text-2xl mb-4">Error de verificación</h1>
            <p className="text-secondary mb-8">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-white text-black hover:bg-gray-200 rounded-full"
              >
                Ir al login
              </Button>
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="w-full rounded-full"
              >
                Registrarse de nuevo
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
